import { Box, Stack, Tooltip } from '@mui/material'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import { track } from '@omnea-app/fe-analytics/useAnalytics'
import { MenuNavItem } from '@omnea-app/fe-app-shell/side-nav/MenuNavItem'
import { NavItemButton } from '@omnea-app/fe-app-shell/side-nav/NavItem'
import { NavDivider } from '@omnea-app/fe-app-shell/side-nav/NavSection'
import { SideNav } from '@omnea-app/fe-app-shell/side-nav/SideNav'
import { SideNavHeader } from '@omnea-app/fe-app-shell/side-nav/SideNavHeader'
import { useSidebarMenuItems, type NavItem as RouteNavItem } from '@omnea-app/fe-app-shell/sidebar/useSidebarMenuItems'
import { useAssessmentsActionNeededCount } from '@omnea-app/fe-assessments/useAssessmentsActionNeededCount'
import { hrefFor } from '@omnea-app/fe-authz/routeHref'
import { useCanCreateRequest } from '@omnea-app/fe-authz/useCanCreateRequest'
import { useRenewalsActionNeededCount } from '@omnea-app/fe-renewals/useRenewalsActionNeededCount'
import { InternalLinkButton } from '@omnea-app/fe-routing/InternalLinkButton'
import { InternalLinkIconButton } from '@omnea-app/fe-routing/InternalLinkIconButton'
import { useGetMyTaskCount } from '@omnea-app/fe-tasks/useTasks'
import {
  CalendarOMIcon,
  CartOMIcon,
  CertificateOMIcon,
  ChartOMIcon,
  ClipboardClockOMIcon,
  ComposeOMIcon,
  ErrorOMIcon,
  FolderOMIcon,
  GridOMIcon,
  HomeOMIcon,
  InvoiceOMIcon,
  LicenseOMIcon,
  NetworkOMIcon,
  PlusOMIcon,
  RenewalOMIcon,
  SettingsOMIcon,
  ShieldCheckOMIcon,
  SirenOMIcon,
  SparkOMIcon,
  StorefrontOMIcon,
  TasksOMIcon,
  TrendUpOMIcon,
  WalletOMIcon,
} from '@omnea-app/icons'
import { useIsDarkMode } from '@omnea-app/omui/useIsDarkMode'
import { settingsHrefFrom } from './settingsHref'
import type { NewRequestInitiatedProperties } from '@omnea-app/fe-requests/analytics'

type NavItemCount = 'assessments' | 'renewals' | 'tasks'

const PRIMARY_ITEMS: readonly RouteNavItem<NavItemCount>[] = [
  { route: 'dashboard', labelKey: 'sidebar.home', Icon: HomeOMIcon },
  { route: 'ai', labelKey: 'sidebar.omnea-ai', Icon: SparkOMIcon },
  { route: 'tasks', labelKey: 'sidebar.tasks', Icon: TasksOMIcon, count: 'tasks' },
  { route: 'startDrafts', labelKey: 'sidebar.drafts', Icon: ComposeOMIcon },
]

const RECORD_ITEMS: readonly RouteNavItem<NavItemCount>[] = [
  { route: 'requestsList', labelKey: 'sidebar.resources-requests', Icon: ClipboardClockOMIcon },
  { route: 'suppliersList', labelKey: 'sidebar.resources-suppliers', Icon: StorefrontOMIcon },
  { route: 'assessmentsList', labelKey: 'sidebar.resources-assessments', Icon: CertificateOMIcon, count: 'assessments' },
  { route: 'agreementsList', labelKey: 'sidebar.resources-agreements', Icon: LicenseOMIcon },
  {
    route: 'insights',
    labelKey: 'sidebar.resources-insights',
    Icon: ChartOMIcon,
    // `/reporting` is the same product under its former address.
    activePaths: ['/insights', '/reporting'],
  },
  {
    labelKey: 'sidebar.resources-risk-management',
    Icon: ShieldCheckOMIcon,
    children: [
      { route: 'risksList', labelKey: 'sidebar.resources-risks', Icon: ErrorOMIcon },
      { route: 'riskAlertsList', labelKey: 'sidebar.resources-alerts', Icon: SirenOMIcon },
      { route: 'subprocessorsList', labelKey: 'sidebar.resources-subprocessors', Icon: NetworkOMIcon },
    ],
  },
  { route: 'documentsList', labelKey: 'sidebar.resources-documents', Icon: FolderOMIcon },
  { route: 'renewalsList', labelKey: 'sidebar.resources-renewals', Icon: RenewalOMIcon, count: 'renewals' },
  { route: 'scheduledRequestsList', labelKey: 'sidebar.resources-scheduled-requests', Icon: CalendarOMIcon },
  { route: 'productCatalogues', labelKey: 'sidebar.resources-product-catalogues', Icon: GridOMIcon },
  {
    route: 'purchaseOrdersList',
    labelKey: 'sidebar.resources-purchase-orders',
    Icon: CartOMIcon,
    badgeKey: 'sidebar.new-badge',
  },
  { route: 'billsList', labelKey: 'sidebar.bills', Icon: InvoiceOMIcon, badgeKey: 'sidebar.new-badge' },
  { route: 'budgetsList', labelKey: 'sidebar.resources-budgets', Icon: WalletOMIcon },
  { route: 'sourcingDashboard', labelKey: 'sidebar.resources-savings-dashboard', Icon: TrendUpOMIcon },
]

type ShellSideNavProps = {
  collapsed: boolean
  onToggleCollapsed: () => void
}

/** The app's navigation in the app shell: every destination the user's routes allow, and New request. */
export const ShellSideNav = ({ collapsed, onToggleCollapsed }: ShellSideNavProps) => {
  const { t } = useTranslation('common')
  const { asPath } = useRouter()
  const canCreateRequest = useCanCreateRequest()

  const { data: assessments = null } = useAssessmentsActionNeededCount()
  const { data: renewals = null } = useRenewalsActionNeededCount()
  const { data: tasks = 0 } = useGetMyTaskCount()
  const counts = { assessments, renewals, tasks }

  const primary = useSidebarMenuItems(PRIMARY_ITEMS, counts)
  const records = useSidebarMenuItems(RECORD_ITEMS, counts)

  const trackCreateRequest = () => track('NewRequestInitiated', { source: 'RequestsCTA' } satisfies NewRequestInitiatedProperties)

  return (
    <SideNav
      aria-label={t('sidebar.main-navigation')}
      collapsed={collapsed}
      header={
        <SideNavHeader
          logo={
            <Link href={hrefFor('dashboard')} style={{ display: 'flex' }}>
              <NavLogo alt={t('sidebar.home')} />
            </Link>
          }
          railLogo={<NavLogo alt="" />}
          expandLabel={t('sidebar.expand-navigation')}
          collapseLabel={t('sidebar.collapse-navigation')}
          onToggleCollapsed={onToggleCollapsed}
        />
      }
      footer={
        <>
          <NavItemButton
            icon={<SettingsOMIcon />}
            label={t('sidebar.settings')}
            href={settingsHrefFrom(asPath)}
            LinkComponent={Link}
          />
          {canCreateRequest &&
            (collapsed ? (
              <Stack alignItems="center">
                <Tooltip title={t('sidebar.create-request')} placement="right">
                  <InternalLinkIconButton
                    href={hrefFor('startRequest')}
                    variant="contained"
                    color="primary"
                    onClick={trackCreateRequest}
                  >
                    <PlusOMIcon />
                  </InternalLinkIconButton>
                </Tooltip>
              </Stack>
            ) : (
              <Box sx={{ px: 1 }}>
                <InternalLinkButton
                  href={hrefFor('startRequest')}
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={<PlusOMIcon />}
                  onClick={trackCreateRequest}
                >
                  {t('sidebar.create-request')}
                </InternalLinkButton>
              </Box>
            ))}
        </>
      }
    >
      {primary.map((item) => (
        <MenuNavItem key={item.href} item={item} />
      ))}
      <NavDivider />
      {records.map((item) => (
        <MenuNavItem key={item.href} item={item} />
      ))}
    </SideNav>
  )
}

const NavLogo = ({ alt }: { alt: string }) => {
  const isDarkMode = useIsDarkMode()

  return (
    <Image
      src={isDarkMode ? '/logo-without-text.svg' : '/app/assets/omnea-monochrome-logo.svg'}
      alt={alt}
      width={30}
      height={19}
      priority
    />
  )
}
